import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import {
  Button,
  Divider,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const BusinessTax = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Essential Forms Resource Center | W-9 & BOI Reporting"
          description="Explore Form W-9 and BOI reporting resources, including instructions, due dates, ownership guidance, reporting requirements, penalties, and more."
          keywords=""
          canonical="resources/essential-forms/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container
        maxWidth="lg"
        sx={{ justifyContent: 'center', mt: 8, mb: 8 }}
        component="section"
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 2,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Essential Forms </span>
          </span>{' '}
          Resource Center
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { md: 18, sm: 16, xs: 16 },
            textAlign: 'center',
            mb: 3,
          }}
        >
          Find clear guidance on Form W-9, BOI Reporting, filing deadlines,
          compliance requirements, and related resources.
        </Typography>

        {/* <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href="#explore"
          >
            Explore Tax Extension Resources
          </Button>
        </Box> */}
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
          scrollMarginTop: 64,
        }}
        spacing={2}
        m={0}
        mt={0}
        component="section"
        id="explore"
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                  textAlign: 'center',
                }}
              >
                Form W-9 Resources
              </Typography>
              <Typography variant="body1" sx={{ textAlign: 'center', mb: 1 }}>
                Comprehensive guidance on completing and filing Form W-9,
                understanding requirements, and meeting compliance standards.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/essential-forms/form-w-9-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#e7eeff',
                      color: '#424aac',
                      fontWeight: 500,
                      border: '1px solid #416ed2',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Taxpayer Information
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form W-9 Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn what Form W-9 is, who may need to complete it, what
                      information is required, and how to properly provide your
                      taxpayer identification information.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Read W-9 Instructions
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
            <Grid size={12}>
              {' '}
              <Divider />{' '}
            </Grid>

            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                  textAlign: 'center',
                }}
              >
                Form 8655 Resources
              </Typography>
              <Typography variant="body1" sx={{ textAlign: 'center', mb: 1 }}>
                Comprehensive guidance on completing Form 8655, understanding
                reporting agent authorization requirements, and meeting IRS
                filing requirements.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/essential-forms/form-8655-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#e7eeff',
                      color: '#424aac',
                      fontWeight: 500,
                      border: '1px solid #416ed2',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Tax Administration
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 8655 Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how to complete Form 8655, Reporting Agent
                      Authorization, and understand the information required for
                      submission.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Read 8655 Instructions
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box component="section" sx={{ my: 8 }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                BOI Reporting Resources
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Essential information on Beneficial Ownership Information
                reporting, compliance requirements, and filing procedures.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/fincen-boir/boi-reporting-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#e7eeff',
                      color: '#424aac',
                      fontWeight: 500,
                      border: '1px solid #416ed2',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Business Compliance
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      BOI Reporting Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand Beneficial Ownership Information reporting
                      requirements, including who may be required to report,
                      what information may be required, and how BOI reporting
                      works.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Read BOIR Instructions
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/fincen-boir/boi-reporting-due-dates/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#e7eeff',
                      color: '#424aac',
                      fontWeight: 500,
                      border: '1px solid #416ed2',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Important Dates
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      BOI Reporting Due Dates
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find applicable BOI reporting deadlines and learn when
                      reporting information may need to be submitted or updated.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Check BOIR Due Dates
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/fincen-boir/company-applicant-vs-beneficial-owner/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#e7eeff',
                      color: '#424aac',
                      fontWeight: 500,
                      border: '1px solid #416ed2',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Ownership Guidance
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Company Applicants vs. Beneficial Owners
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand the difference between company applicants and
                      beneficial owners, including their roles and how they
                      relate to Beneficial Ownership Information reporting.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn the Difference
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/fincen-boir/boir-penalties/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#e7eeff',
                      color: '#424aac',
                      fontWeight: 500,
                      border: '1px solid #416ed2',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Compliance & Penalties
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      BOI Reporting Penalties and Charges
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn about potential penalties and charges associated
                      with BOI reporting and why accurate and timely reporting
                      is important for applicable businesses.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About BOI Penalties
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ backgroundColor: '#f0f2f9' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
          >
            <Box
              sx={{
                width: '100%',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Need More Help with Essential Forms?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 2,
                }}
              >
                Not sure which form applies to your situation or need
                clarification on BOI requirements? Our experts are here to help
                you navigate these essential compliance requirements.
              </Typography>

              <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 1,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=essential-forms`}
                >
                  Get Started Now
                </Button>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>
    </AppLayout>
  );
};

export default BusinessTax;
